/**
 * Impostazioni dei canali del responsabile di canale (Task 46), area
 * rappresentante: `/area-associativa/supporto/canali`.
 *
 * Per ogni canale di cui il rappresentante e componente (`channels/manage`,
 * permesso `associativa.supporto.manage_channel`):
 *  - «Chi può rispondere all'associato»: elenco componenti con avatar e
 *    l'interruttore «Può rispondere all'associato» (PATCH). Chi non puo
 *    rispondere scrive bozze che il responsabile approva. Gli utenti admin
 *    rispondono sempre: per loro il flag non si applica (`canReplyApplies`);
 *  - «Notifiche e visualizzazione» (Task 47): gli interruttori di
 *    `CHANNEL_SETTING_FIELDS` (`ChannelSettingToggle`), salvati al cambio con
 *    `setChannelSettings` (PATCH parziale): aggiornamento ottimistico, toast,
 *    rollback se il salvataggio fallisce.
 *
 * Stile della shell associativa (Tailwind, dark via `.dark`), font piccoli.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Loader2, MessageSquareReply, Users } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';
import ChannelAvatarMark from './ChannelAvatarMark';
import ChannelSettingToggle from './ChannelSettingToggle';
import { CHANNEL_SETTING_FIELDS, settingValue, type ChannelSettingKey } from './channelSettingsFields';
import { initialsOf } from './constants';
import { photoUrl } from '../../utils/photoUrl';
import type { ChannelSettings, ManagedChannel, ManagedChannelMember, OperatorSupportApi } from '../../services/supportApi';

export interface ChannelSettingsPanelProps {
  api: Pick<OperatorSupportApi, 'getManagedChannels' | 'setMemberCanReply' | 'setChannelSettings'>;
  /** Id del rappresentante corrente: togliere il flag a se stessi chiede conferma. */
  selfId?: string;
}

const CARD = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5';
const SECTION_TITLE = 'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const MUTED = 'text-xs text-gray-500 dark:text-gray-400';

export const FORBIDDEN_CHANNELS_TEXT =
  'Le impostazioni dei canali sono riservate al responsabile del canale. Se pensi che ti servano, chiedi alla segreteria nazionale di abilitare il permesso «Responsabile canale» per la tua carica.';

const MemberAvatar: React.FC<{ m: ManagedChannelMember }> = ({ m }) => {
  const [broken, setBroken] = useState(false);
  const src = photoUrl(m.foto);
  const parts = (m.nome || '').trim().split(/\s+/);
  const ini = initialsOf(parts[0], parts.slice(1).join(' '));
  return src && !broken ? (
    <img
      src={src}
      alt=""
      onError={() => setBroken(true)}
      className="w-8 h-8 rounded-full object-cover flex-none"
    />
  ) : (
    <span
      aria-hidden="true"
      className="w-8 h-8 rounded-full flex-none inline-flex items-center justify-center text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
    >
      {ini}
    </span>
  );
};

const ChannelSettingsPanel: React.FC<ChannelSettingsPanelProps> = ({ api, selfId }) => {
  const toast = useToast();
  const [channels, setChannels] = useState<ManagedChannel[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  /** Richiesta di togliere il flag a se stessi, in attesa di conferma. */
  const [selfOff, setSelfOff] = useState<{ channel: ManagedChannel; member: ManagedChannelMember } | null>(null);

  const load = useCallback(async () => {
    if (!api.getManagedChannels) {
      setChannels([]);
      return;
    }
    try {
      setChannels(await api.getManagedChannels());
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setForbidden(true);
        setChannels([]);
        return;
      }
      toast.error(err?.response?.data?.error || 'Errore caricamento canali');
      setChannels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = async (channel: ManagedChannel, member: ManagedChannelMember, canReply: boolean) => {
    if (!api.setMemberCanReply) return;
    const key = `${channel._id}:${member.actorId}`;
    setSaving(key);
    try {
      await api.setMemberCanReply(channel._id, member.actorId, canReply);
      setChannels((prev) =>
        (prev || []).map((c) =>
          c._id !== channel._id
            ? c
            : { ...c, members: c.members.map((m) => (m.actorId === member.actorId ? { ...m, canReply } : m)) }
        )
      );
      toast.success(
        canReply
          ? `${member.nome} ora risponde direttamente all'associato`
          : `Le risposte di ${member.nome} diventano bozze da approvare`
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Errore salvataggio impostazione');
    } finally {
      setSaving(null);
    }
  };

  const patchSettings = (channelId: string, settings: ChannelSettings) =>
    setChannels((prev) => (prev || []).map((c) => (c._id === channelId ? { ...c, settings } : c)));

  const onSettingChange = async (channel: ManagedChannel, key: ChannelSettingKey, next: boolean) => {
    if (!api.setChannelSettings) return;
    const previous = channel.settings;
    const savingKey = `${channel._id}:settings:${key}`;
    setSaving(savingKey);
    patchSettings(channel._id, { ...previous, [key]: next });
    try {
      const saved = await api.setChannelSettings(channel._id, { [key]: next });
      if (saved) patchSettings(channel._id, saved);
      toast.success(next ? 'Impostazione attivata' : 'Impostazione disattivata');
    } catch (err: any) {
      patchSettings(channel._id, previous);
      toast.error(err?.response?.data?.error || 'Errore salvataggio impostazione');
    } finally {
      setSaving(null);
    }
  };

  const onToggle = (channel: ManagedChannel, member: ManagedChannelMember) => {
    const next = !member.canReply;
    if (!next && selfId && String(member.actorId) === String(selfId)) {
      setSelfOff({ channel, member });
      return;
    }
    void apply(channel, member, next);
  };

  if (channels === null) {
    return (
      <div className={`${CARD} flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300`}>
        <Loader2 size={14} className="animate-spin" aria-hidden="true" /> Caricamento…
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5 max-w-xl" role="alert">
        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Funzione non abilitata per la tua carica</p>
        <p className="text-sm text-amber-900/80 dark:text-amber-200/80 mt-1">{FORBIDDEN_CHANNELS_TEXT}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-base font-semibold text-gray-900 dark:text-white">Impostazioni dei canali</h1>
        <p className={`${MUTED} mt-1`}>
          Sei responsabile dei canali qui sotto. Chi non può rispondere all&apos;associato scrive risposte in bozza:
          le leggi, le correggi se serve e le approvi (o le rifiuti) dalla scheda del ticket.
        </p>
      </div>

      {channels.length === 0 && (
        <div className={`${CARD} text-sm text-gray-600 dark:text-gray-300`}>Nessun canale da gestire.</div>
      )}

      {channels.map((c) => (
        <section key={c._id} className={CARD} aria-label={`Canale ${c.nome}`}>
          <div className="flex items-center gap-2 mb-3">
            <ChannelAvatarMark channel={c} size={18} />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{c.nome}</h2>
            {!c.isActive && (
              <span className="text-2xs font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Disattivato
              </span>
            )}
          </div>

          <div data-section="risposte" className="flex flex-col gap-2">
            <p className={SECTION_TITLE}>
              <MessageSquareReply size={12} aria-hidden="true" /> Chi può rispondere all&apos;associato
            </p>
            {c.members.length === 0 ? (
              <p className={MUTED}>Nessun componente.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {c.members.map((m) => {
                  const key = `${c._id}:${m.actorId}`;
                  const isSelf = Boolean(selfId) && String(m.actorId) === String(selfId);
                  return (
                    <li key={key} className="flex items-center gap-3 py-2">
                      <MemberAvatar m={m} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-800 dark:text-gray-100 truncate">
                          {m.nome}
                          {isSelf && <span className={`${MUTED} ml-1`}>(tu)</span>}
                          {!m.active && <span className={`${MUTED} ml-1`}>· non attivo</span>}
                        </p>
                        <p className={`${MUTED} truncate`}>
                          {!m.canReplyApplies
                            ? 'Utente amministrazione · risponde sempre direttamente'
                            : m.canReply
                              ? 'Risponde direttamente'
                              : 'Le sue risposte diventano bozze da approvare'}
                        </p>
                      </div>
                      {m.canReplyApplies && (
                        <div className="flex items-center gap-2 flex-none">
                          <span className="text-xs text-gray-600 dark:text-gray-300 hidden sm:inline">Può rispondere all&apos;associato</span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={m.canReply}
                            aria-label={`Può rispondere all'associato: ${m.nome}`}
                            disabled={saving === key}
                            onClick={() => onToggle(c, m)}
                            className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors disabled:opacity-50 ${
                              m.canReply ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                          >
                            <span
                              aria-hidden="true"
                              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                m.canReply ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            data-section="notifiche-visualizzazione"
            className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-col gap-1"
          >
            <p className={SECTION_TITLE}>
              <Bell size={12} aria-hidden="true" /> Notifiche e visualizzazione
            </p>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {CHANNEL_SETTING_FIELDS.map((f) => (
                <ChannelSettingToggle
                  key={f.key}
                  label={f.label}
                  hint={f.hint}
                  channelName={c.nome}
                  checked={settingValue(c.settings, f.key)}
                  disabled={!api.setChannelSettings || saving === `${c._id}:settings:${f.key}`}
                  onChange={(next) => void onSettingChange(c, f.key, next)}
                />
              ))}
            </div>
          </div>
        </section>
      ))}

      <p className={`${MUTED} flex items-center gap-1`}>
        <Users size={12} aria-hidden="true" /> I componenti dei canali li aggiunge o toglie la segreteria nazionale.
      </p>

      <ConfirmDialog
        isOpen={selfOff !== null}
        onClose={() => setSelfOff(null)}
        onConfirm={() => {
          if (selfOff) void apply(selfOff.channel, selfOff.member, false);
        }}
        title="Togli il permesso a te stesso"
        message={`Stai togliendo a te stesso «Può rispondere all'associato» sul canale ${selfOff?.channel.nome || ''}: le tue prossime risposte diventeranno bozze da approvare. Potrai riattivarlo da qui.`}
        confirmText="Togli il permesso"
        type="warning"
      />
    </div>
  );
};

export default ChannelSettingsPanel;
